/* ============================================================
   KOS Deployment Tracker — backend client
   ------------------------------------------------------------
   Talks to the AgentKai Flask blueprint mounted at /kos-tracker:
     GET  /kos-tracker/data          -> latest published dataset (or 204)
     POST /kos-tracker/data          -> publish a dataset (shared default)
     GET  /kos-tracker/proxy?url=..  -> fetch a public .xlsx (CORS fallback)
   ============================================================ */
import axios from "axios";
import { BACKEND_BASE_URL } from "../../constants";

const BASE = String(BACKEND_BASE_URL || "").replace(/\/+$/, "");
const url = (p) => `${BASE}/${p.replace(/^\/+/, "")}`;

/** Fetch the latest published dataset. Returns null if none exists. */
export async function fetchPublishedData() {
  try {
    const res = await axios.get(url("kos-tracker/data"), {
      timeout: 20000,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 204 || s === 404,
    });
    if (res.status === 204 || res.status === 404 || !res.data) return null;
    const payload = res.data.data || res.data;
    if (payload && Array.isArray(payload.records) && payload.records.length) {
      return payload;
    }
    return null;
  } catch (e) {
    // backend optional — fail soft so the dashboard still loads sample data
    console.warn("[KOS] could not fetch published data:", e.message);
    return null;
  }
}

/** Publish a dataset so it becomes the shared default for all users. */
export async function publishData(data) {
  const body = {
    records: data.records,
    months: data.months || [],
    periodLabel: data.periodLabel || "",
    sourceLabel: data.sourceLabel || "Uploaded workbook",
    sourceUrl: data.sourceUrl || "",
  };
  const res = await axios.post(url("kos-tracker/data"), body, {
    timeout: 30000,
    headers: { "Content-Type": "application/json" },
  });
  return res.data;
}

/**
 * Fetch a public .xlsx URL.
 * Tries a direct browser fetch first; on CORS/network failure falls back
 * to the backend proxy. Returns an ArrayBuffer.
 */
export async function fetchRemoteWorkbook(link) {
  // 1) direct attempt (works for CORS-enabled / same-origin links)
  try {
    const res = await fetch(link, { cache: "no-cache" });
    if (res.ok) {
      const buf = await res.arrayBuffer();
      if (buf && buf.byteLength > 0) return buf;
    }
  } catch (e) {
    /* fall through to proxy */
  }

  // 2) backend proxy fallback (handles CORS-restricted public links)
  const res = await axios.get(url("kos-tracker/proxy"), {
    params: { url: link },
    responseType: "arraybuffer",
    timeout: 45000,
  });
  return res.data;
}
